/* ============================================================================
   19 · Newsroom — the press page as a Shopify blog index
   Figma 9Dhvc9N1v5FWO9b54mpCkN · D Newsroom 4723:131256 · M Newsroom 4723:131287
   ----------------------------------------------------------------------------
   What's being decided here:

     • The Newsroom as a Shopify blog (blog.newsroom) instead of a Contentful
       page: the blog title is the h1, the count is blog.articles_count, the
       grid is {% paginate blog.articles by 12 %}, each card is one link with
       a <time datetime>, and the JSON-LD is a Blog of BlogPostings. The
       theme port is in theme/ (section, snippet, JSON template, locales).
     • News cards with depth: magoni.info's cursor effect, per card, on
       desktop hover (depth.js) — the photo and the copy slide past the pill
       outline in opposite directions as the card turns toward the cursor.
     • Load More over real ?page= links, so the archive stays crawlable and
       works without JS; it counts what's shown against the blog's total.

   Motion is the Homepage / PLP / PDP vocabulary (mo.js, motion.dev): the
   first screen rises 16 on --mo-slow while the ground settles from 1.04,
   the title rises out of its mask, every row of cards reveals left to
   right as it enters, the photo pushes in on hover. Reduced motion keeps
   every fade, drops the travel and the tilt; nothing is ever left hidden.
   ========================================================================== */

import { mountFigsNav } from '/src/figs-nav/figs-nav.js';
import {
  animate, inView, hover, press, stagger, MO, state, tween, play, track, teardown, setSpeed, clearTracked, $, $$, byPosition,
} from '../10-pdp-prototype/mo.js';
import { BLOG, ARTICLES } from './data.js';
import { armDepth, settleDepth } from './depth.js';

/* ============================================================
   HEADER + FOOTER — the shared nav, white on the dark ground,
   and the shared site footer
   ============================================================ */
mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  footer: $('[data-footer]'),
  theme: 'dark',
});

/* ============================================================
   THE BLOG — Shopify's paginate, read from ?page=
   ============================================================ */
const grid = $('[data-news-grid]');
const more = $('[data-news-more]');
const moreBtn = $('[data-more-btn]');
const total = ARTICLES.length;
const pages = Math.ceil(total / BLOG.perPage);
const params = new URLSearchParams(location.search);
const firstPage = Math.min(pages, Math.max(1, parseInt(params.get('page'), 10) || 1));
let lastPage = firstPage;

/* article.published_at | time_tag: format: 'month_day_year' → "October 30, 2026" */
const longDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
/* Figma's Title case (textCase TITLE) on the card titles: a capital at the
   start of every word and after a hyphen or an opening bracket or quote —
   "Back-To-School", "F(All)", "“Sexiest" — but never after an apostrophe,
   which CSS capitalize gets wrong ("2026’S", "F’Ing").
   snippets/title-case.liquid does the same on the theme. */
const titleCase = (s) => s.replace(/(^|[\s\-–—(\[“"‘/])(\p{Ll})/gu, (m, pre, ch) => pre + ch.toUpperCase());

/* snippets/news-card.liquid, as HTML. The first row is the page's LCP:
   eager, high priority; the rest lazy. */
const cardHTML = (a, i) => {
  const eager = i < 3;
  return `
      <li class="newsroom-grid__item">
        <article class="news-card" data-news-card aria-labelledby="news-card-${a.handle}">
          <a class="news-card__link" href="${esc(a.url)}" target="_blank" rel="noopener" data-news-link>
            <div class="news-card__stage" data-news-stage>
              <div class="news-card__media">
                <img class="news-card__img" src="${a.image.src}" alt="" width="${a.image.width}" height="${a.image.height}"
                  sizes="(min-width: 780px) 275px, calc(100vw - 80px)" ${eager ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async" data-news-img>
              </div>
              <span class="news-card__outline" aria-hidden="true"></span>
              <div class="news-card__copy">
                <h2 class="news-card__title" id="news-card-${a.handle}">${esc(titleCase(a.title))}</h2>
                <time class="news-card__date" datetime="${a.published_at}">${longDate.format(new Date(a.published_at))}</time>
                <span class="news-card__more" aria-hidden="true" data-news-more-label><span class="pdp-link news-card__more-link"><span class="trim">learn more</span></span></span>
              </div>
            </div>
          </a>
        </article>
      </li>`;
};

const pageItems = (p) => ARTICLES.slice((p - 1) * BLOG.perPage, p * BLOG.perPage);

function renderPage(p, { append }) {
  const items = pageItems(p);
  const html = items.map((a, i) => cardHTML(a, append ? 99 : i)).join('');
  if (append) grid.insertAdjacentHTML('beforeend', html);
  else grid.innerHTML = html;
  armDepth(grid);
  bindCardHovers();
  return items;
}

/* "12/31" — what's on screen against blog.articles_count (Figma's "12/36"),
   said in full to screen readers */
function syncMore() {
  const from = (firstPage - 1) * BLOG.perPage + 1;
  const to = Math.min(total, lastPage * BLOG.perPage);
  const range = from === 1 ? `${to}` : `${from}–${to}`;
  $('[data-more-count]').textContent = `${range}/${total}`;
  $('[data-more-sr]').textContent = `Showing ${from === 1 ? to : `${from} to ${to}`} of ${total} articles`;
  if (lastPage >= pages) {
    moreBtn.hidden = true;
    moreBtn.removeAttribute('href');
  } else {
    moreBtn.hidden = false;
    moreBtn.href = `?page=${lastPage + 1}`;
  }
}

/* The JSON-LD blog.liquid prints: a Blog of the BlogPostings on this page */
function syncJsonLd() {
  const shown = ARTICLES.slice((firstPage - 1) * BLOG.perPage, lastPage * BLOG.perPage);
  $('[data-blog-jsonld]').textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `FIGS ${BLOG.title}`,
    url: `https://www.wearfigs.com${BLOG.url}`,
    publisher: { '@type': 'Organization', name: 'FIGS', url: 'https://www.wearfigs.com' },
    blogPost: shown.map((a) => ({
      '@type': 'BlogPosting',
      headline: a.title,
      datePublished: a.published_at,
      url: a.url,
      image: new URL(a.image.src, location.href).href,
    })),
  });
}

function initBlog() {
  $('[data-article-count]').textContent = String(total);
  renderPage(firstPage, { append: false });
  syncMore();
  syncJsonLd();

  moreBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (lastPage >= pages || moreBtn.getAttribute('aria-busy') === 'true') return;
    moreBtn.setAttribute('aria-busy', 'true');
    const before = grid.children.length;
    lastPage += 1;
    renderPage(lastPage, { append: true });
    syncMore();
    syncJsonLd();
    armCardReveals();
    moreBtn.removeAttribute('aria-busy');
    /* keyboard and screen-reader users land on the first new article */
    $('[data-news-link]', grid.children[before])?.focus({ preventScroll: true });
  });
}

/* ============================================================
   CARD HOVER — the Homepage's push-in on the photo, and the
   Figma's hidden "learn more" layer rising in (desktop); a
   press sinks the card like the lab's buttons (every pointer)
   ============================================================ */
const hovered = new WeakSet();
function bindCardHovers() {
  $$('[data-news-card]', grid).forEach((card) => {
    if (hovered.has(card)) return;
    hovered.add(card);
    const link = $('[data-news-link]', card);
    const img = $('[data-news-img]', card);
    const label = $('[data-news-more-label]', card);
    teardown.push(hover(link, () => {
      play(img, { scale: MO.scaleIn }, { duration: MO.deliberate, ease: MO.eEntrance });
      play(label, { opacity: 1, y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance });
      return () => {
        play(img, { scale: 1 }, { duration: MO.deliberate, ease: MO.eEntrance });
        play(label, { opacity: 0, y: 0 }, { duration: MO.quick, ease: MO.eStandard });
      };
    }));
    teardown.push(press(link, () => {
      play(card, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard });
      return () => track(animate(card, { scale: 1 }, state.reduced ? { duration: MO.instant } : { type: 'spring', stiffness: 480, damping: 18 }));
    }));
  });
}

/* ============================================================
   MOTION — first-screen intro + scroll reveals
   ============================================================ */
let introAt = 0;
function initIntro() {
  introAt = performance.now();
  /* the ground fades in and settles, like the Homepage hero photo */
  tween($('[data-backdrop]'), { opacity: [0, 1], scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance });
  /* the title rises out of its mask; the side labels rise 16 behind it */
  tween($('[data-intro-line]'), { y: ['140%', '0%'] }, { duration: MO.deliberate, delay: 0.12, ease: MO.eEntrance });
  tween($$('[data-intro]').sort(byPosition), { y: [MO.riseSm, 0], opacity: [0, 1] },
    { duration: MO.slow, delay: stagger(MO.stagTight, { startDelay: 0.24 }), ease: MO.eEntrance });
}
/* cards on the first screen queue behind the title; later rows go at once */
const cardDelay = () => Math.max(0, 0.36 - (performance.now() - introAt) / 1000);

/* Each row of cards rises, fades and unclips left to right as one gesture
   (the Homepage rail's entrance), while its photos settle from 1.04. Cards
   that enter view in the same frame are one batch, sorted by position. */
const armed = new WeakSet();
function armCardReveals() {
  let queue = [];
  let raf = 0;
  const flush = () => {
    raf = 0;
    const batch = queue.sort(byPosition);
    queue = [];
    const delay = stagger(MO.stagBase, { startDelay: cardDelay() });
    /* the clip comes off once the row is in: left on, it would flatten the
       card's 3D and cut the layers that slide past its edge on hover */
    tween(batch, { opacity: [0, 1], y: [MO.riseMd, 0], clipPath: ['inset(0 0 12% 0)', 'inset(0 0 0% 0)'] },
      { duration: MO.deliberate, delay, ease: MO.eEntrance })
      ?.finished.then(() => batch.forEach((c) => { c.style.clipPath = 'none'; }));
    tween(batch.map((c) => $('[data-news-img]', c)), { scale: [MO.scaleIn, 1] },
      { duration: MO.cinematic, delay, ease: MO.eEntrance });
  };
  $$('[data-news-card]', grid).forEach((card) => {
    if (armed.has(card)) return;
    armed.add(card);
    teardown.push(inView(card, () => {
      queue.push(card);
      if (!raf) raf = requestAnimationFrame(flush);
    }, { margin: '0px 0px -8% 0px' }));
  });
}

function armReveals() {
  armCardReveals();
  if (!armed.has(more)) {
    armed.add(more);
    teardown.push(inView(more, () => {
      tween(more, { opacity: [0, 1], y: [MO.riseSm, 0] }, { duration: MO.slow, ease: MO.eEntrance });
    }, { margin: '0px 0px -10% 0px' }));
  }
}

/* ============================================================
   LAB BAR — hidden on templates; H shows it
   ============================================================ */
function initLabBar() {
  const labBar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    settleDepth(grid);
    $$('[data-news-card]', grid).forEach((c) => hovered.delete(c));
    $$('[data-news-img], [data-news-more-label]', grid).forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
    /* the title goes back below its mask; everything else hides through
       motion, so its cached opacity agrees on the replay */
    animate($('[data-intro-line]'), { y: '140%' }, { duration: 0 });
    [...$$('[data-news-card]', grid), ...$$('[data-intro]'), more, $('[data-backdrop]')].forEach((el) => {
      animate(el, { opacity: 0 }, { duration: 0 });
      armed.delete(el);
    });
    requestAnimationFrame(() => { bindCardHovers(); boot(); });
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => { killed = true; handle.hidden = true; labBar.remove(); });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable) return;
    const hide = !labBar.hidden;
    labBar.hidden = hide;
    handle.hidden = !hide;
  });
  handle.addEventListener('click', () => { if (killed) return; labBar.hidden = false; handle.hidden = true; });
}

/* ============================================================
   BOOT
   ============================================================ */
function initHoverButtons() {
  /* Button hover — the Homepage hero's (08 initMicro): scale up on quick,
     press down, spring back */
  $$('[data-hover-btn]').forEach((b) => {
    teardown.push(hover(b, () => {
      track(animate(b, { scale: MO.scaleHover }, { duration: MO.quick, ease: MO.eEntrance }));
      return () => track(animate(b, { scale: 1 }, { duration: MO.quick, ease: MO.eEntrance }));
    }));
    teardown.push(press(b, () => {
      track(animate(b, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard }));
      return () => track(animate(b, { scale: 1 }, state.reduced ? { duration: MO.instant } : { type: 'spring', stiffness: 480, damping: 18 }));
    }));
  });
}

function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  initIntro();
  armReveals();
  initHoverButtons();
}

initBlog();
boot();
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();

addEventListener('load', () => {
  const broken = $$('img').filter((i) => i.getAttribute('src') && i.complete && i.naturalWidth === 0);
  if (broken.length) console.error(`[assets] ${broken.length} image(s) failed to load`, broken.map((i) => i.src));
  else console.info(`[assets] ${$$('img').length} images OK`);
});
console.info('%cFIGS Motion Lab · 19 Newsroom', 'font-weight:700',
  `\n${total} articles · page ${firstPage} of ${pages}`, '\nReduced:', state.reduced);
